import { AlertTriangle, Inbox } from 'lucide-react';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { useTranslation } from '../../../application/hooks/useTranslation';
import './QueryState.css';

export interface QueryStateProps {
  loading: boolean;
  error: unknown;
  empty?: boolean;
  onRetry?: () => void;
  skeletonCount?: number;
  /** Optional custom error message (else derived from error). */
  errorMessage?: string;
}

type Translate = (key: string) => string;

/** Map HTTP-ish errors to a localized user-facing message (DEVELOPMENT_PLAN §17.2). */
export function errorMessageOf(error: unknown, fallback: string, t: Translate): string {
  if (error && typeof error === 'object') {
    const e = error as { response?: { status?: number; data?: { detail?: string } }; message?: string };
    const detail = e.response?.data?.detail;
    if (typeof detail === 'string' && detail) return detail;
    switch (e.response?.status) {
      case 401:
        return t('auth.sessionExpired');
      case 403:
        return t('auth.unauthorized');
      case 404:
        return t('errors.notFound');
      case 422:
        return t('errors.invalid');
      case 429:
        return t('errors.tooManyRequests');
      default:
        if (e.response && typeof e.response.status === 'number' && e.response.status >= 500) {
          return t('errors.server');
        }
        if (e.message === 'Network Error') return t('errors.network');
    }
  }
  return fallback;
}

/**
 * Standard loading/error/empty gate for every query-backed block (knowledge-tree edge cases).
 * Renders exactly one of: skeleton / error+retry / empty / children.
 */
export function QueryState({ loading, error, empty = false, onRetry, skeletonCount = 3, errorMessage }: QueryStateProps) {
  const { t } = useTranslation();

  if (loading) return <Skeleton count={skeletonCount} />;
  if (error) {
    return (
      <div className="state-box state-error" role="alert" data-testid="error-state">
        <AlertTriangle size={20} aria-hidden />
        <p>{errorMessage ?? errorMessageOf(error, t('common.error'), t)}</p>
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry} data-testid="retry-button">
            {t('common.retry')}
          </Button>
        )}
      </div>
    );
  }
  if (empty) {
    return (
      <div className="state-box state-empty" data-testid="empty-state">
        <Inbox size={20} aria-hidden />
        <p>{t('common.empty')}</p>
      </div>
    );
  }
  return null;
}
